/**
 * ExperienceLayerShell — Sprint FOS-10b.
 *
 * Renders the Experience Layer (`/api/experience/*`) as ONE calm,
 * phase-aware primary surface. Not a dashboard catalogue.
 *
 * Operator boundaries (FOS-10b decision letter):
 *   • Backend state is authoritative. The shell reads the phase from
 *     `/api/experience/context-ribbon` and requests only the tile(s)
 *     that match the current operational moment.
 *   • Morning phase           → Morning Digest prominent.
 *   • End-of-day phase        → Tomorrow → Close of Day →
 *                               Next-Lesson Preparation, ONE ordered
 *                               sequence.
 *   • Lesson / travel / other → silent. Work Mode + existing home
 *                               header own those moments; the shell
 *                               must not duplicate them.
 *   • Sections that duplicate the InstructorHomeHeader ("today's
 *     diary" / "next lesson") are filtered out here. The shell adds
 *     attention items the header does not surface (compliance,
 *     payments, pending actions, end-of-day sequence).
 *
 * The shell is admin-only in FOS-1 (mirrors backend `_admin_only`).
 * Non-admin instructors will silently see nothing until the
 * backend surface widens — expected, not an error.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Sunrise, MoonStar, ChevronDown, ChevronRight,
  AlertTriangle, CheckCircle2, Calendar, Wallet,
  ListTodo, BookOpen, Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Section kinds already surfaced by InstructorHomeHeader — skip these
// to satisfy the "one visible authoritative presentation" rule.
const DUPLICATE_KINDS = new Set(["first_lesson", "day_shape"]);

// Calm section kinds — closed gates that add no signal are hidden.
// Every gate that is still "waiting_external" MUST be surfaced per
// the FOS-4 doctrine ("Declared external dependencies must always
// be visible — their absence must never become a silent green.").
const SILENT_WHEN_CLOSED_KINDS = new Set([
  "debrief_gate", "homework_gate", "payment_gate",
  "pending_action_gate", "upstream_summary",
]);

const KIND_ICON = {
  compliance:                   AlertTriangle,
  payment_attention:            Wallet,
  outstanding_pending_actions:  ListTodo,
  external_gate:                AlertTriangle,
  green_light_state:            CheckCircle2,
  no_bookings_tomorrow:         Calendar,
  no_lesson_tomorrow:           Calendar,
  next_lesson_scheduled:        Calendar,
  focus_reminder:               BookOpen,
};

const EXTERNAL_GATE_LABEL = {
  device_navigation:         "Device navigation — instructor-owned",
  business_bank_access:      "Business bank access — instructor-owned",
  provider_outbound_comms:   "Outbound comms provider — instructor-owned",
};

function labelForSection(sec) {
  switch (sec.kind) {
    case "compliance":
      return `${sec.label || "Compliance"} due in ${sec.days_until ?? "—"} day${
        sec.days_until === 1 ? "" : "s"
      }`;
    case "payment_attention":
      return `${sec.count} payment issue${sec.count === 1 ? "" : "s"} awaiting resolution`;
    case "outstanding_pending_actions":
      return `${sec.count} pending action${sec.count === 1 ? "" : "s"} still open`;
    case "no_bookings_tomorrow":
      return "No bookings tomorrow — nothing to prepare.";
    case "no_lesson_tomorrow":
      return "No lesson tomorrow.";
    case "green_light_state":
      return sec.green_light
        ? "Green light — safe to close the day."
        : "Not yet green — see gates below.";
    case "external_gate":
      return EXTERNAL_GATE_LABEL[sec.external] || sec.external || "External dependency";
    case "next_lesson_scheduled":
      return sec.starts_at
        ? `Next lesson at ${sec.starts_at}`
        : "Next lesson scheduled.";
    case "focus_reminder":
      return sec.headline || "Focus reminder ready.";
    case "declared_external_dependency":
      return sec.label || sec.gate || "External dependency";
    default:
      // Fall back to a neutral summary — never invent copy.
      return sec.label || sec.headline || sec.kind.replace(/_/g, " ");
  }
}

function keepSection(sec) {
  if (DUPLICATE_KINDS.has(sec.kind)) return false;
  // Hide gates that are cleanly closed (platform_done). Keep gates
  // waiting on external systems so they never silently disappear.
  if (SILENT_WHEN_CLOSED_KINDS.has(sec.kind)
      && sec.action_status === "platform_done") {
    return false;
  }
  return true;
}

function TileRow({ tile, hint }) {
  const sections = (tile?.sections || []).filter(keepSection);
  if (sections.length === 0) {
    return (
      <div
        data-testid={`experience-tile-${tile?.tile_type || "unknown"}-calm`}
        className="flex items-center gap-2 text-xs text-emerald-300 px-4 py-2.5 bg-emerald-900/15 border border-emerald-800/30 rounded-xl"
      >
        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
        <span>{tile?.headline || hint || "Nothing needs your attention."}</span>
      </div>
    );
  }
  return (
    <div
      data-testid={`experience-tile-${tile?.tile_type || "unknown"}`}
      className="rounded-2xl border border-slate-700 bg-slate-800/60 px-4 py-3"
    >
      <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-2">
        {tile?.headline || hint || tile?.tile_type?.replace(/_/g, " ")}
      </p>
      <ul className="space-y-1.5">
        {sections.map((sec, i) => {
          const Icon = KIND_ICON[sec.kind] || Sparkles;
          return (
            <li
              key={`${tile.tile_id || tile.tile_type}-${i}`}
              data-testid={`experience-section-${sec.kind}`}
              className="flex items-start gap-2 text-sm text-slate-200"
            >
              <Icon className="w-4 h-4 mt-0.5 text-blue-300 flex-shrink-0" />
              <span>{labelForSection(sec)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function ExperienceLayerShell({ user }) {
  const [phase, setPhase] = useState(null);
  const [primary, setPrimary] = useState(null);
  const [endOfDay, setEndOfDay] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [accessible, setAccessible] = useState(true);

  const load = useCallback(async () => {
    try {
      const ribbon = await axios.get(`${API}/api/experience/context-ribbon`, {
        withCredentials: true,
      });
      const currentPhase = ribbon.data?.phase || null;
      setPhase(currentPhase);

      if (currentPhase === "morning") {
        const today = await axios.get(`${API}/api/experience/today`, {
          withCredentials: true,
        });
        setPrimary(today.data?.primary_tile || null);
        setEndOfDay(null);
      } else if (currentPhase === "end_of_day") {
        const eod = await axios.get(`${API}/api/experience/end-of-day`, {
          withCredentials: true,
        });
        setEndOfDay(eod.data || null);
        setPrimary(null);
      } else {
        // Lesson / travel / business / completion — the shell stays
        // silent by design. Work Mode + the header own these moments.
        setPrimary(null);
        setEndOfDay(null);
      }
    } catch (err) {
      // 403 is the expected shape for non-admin callers in FOS-1.
      // Any other error is silent — the shell must never break the
      // page (per operator: "calm workflow, not another dashboard").
      if (err?.response?.status === 403) {
        setAccessible(false);
      }
      setPrimary(null);
      setEndOfDay(null);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Silent-render contract:
  //  • not loaded yet  → render nothing (avoid layout flash)
  //  • not accessible  → render nothing (calm degrade for non-admin)
  //  • no phase-scoped payload → render nothing
  if (!loaded || !accessible) return null;
  if (!primary && !endOfDay) return null;

  const isMorning = phase === "morning" && primary;
  const isEndOfDay = phase === "end_of_day" && endOfDay;

  return (
    <section
      data-testid="experience-layer-shell"
      data-phase={phase || "unknown"}
      className="rounded-2xl border border-slate-700 bg-gradient-to-br from-slate-900 to-slate-800 p-4 space-y-3"
    >
      <button
        type="button"
        data-testid="experience-layer-shell-toggle"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-600/20 flex items-center justify-center">
            {isMorning ? (
              <Sunrise className="w-5 h-5 text-blue-300" />
            ) : (
              <MoonStar className="w-5 h-5 text-indigo-300" />
            )}
          </div>
          <div>
            <p className="text-sm font-bold text-white leading-tight">
              {isMorning ? "This morning" : "Closing the day"}
            </p>
            <p className="text-[11px] text-slate-400 leading-tight">
              {isMorning
                ? "One calm read of what needs you first."
                : "Tomorrow → close of day → next-lesson prep."}
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-400" />
        )}
      </button>

      {expanded && (
        <div className="space-y-3">
          {isMorning && (
            <TileRow tile={primary} hint="This morning · Morning Digest" />
          )}

          {isEndOfDay && (
            <>
              <TileRow
                tile={endOfDay.tomorrow}
                hint="Tomorrow · what still needs attention"
              />
              <TileRow
                tile={endOfDay.close_of_day}
                hint="Close of day · safe to close?"
              />
              <TileRow
                tile={endOfDay.next_lesson_preparation}
                hint="Next lesson · what is already prepared"
              />
              {endOfDay.sequence_narrative && (
                <p
                  data-testid="experience-eod-narrative"
                  className="text-[11px] text-slate-500 italic leading-snug"
                >
                  {endOfDay.sequence_narrative}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
